import React from 'react';
import { Pressable, StyleProp, ViewStyle } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { shadow } from '../../assets/theme';
import { makeStyles, useAppTheme } from '../../theme/ThemeContext';

type IconCircleButtonProps = {
  icon: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  onPress: () => void;
  accessibilityLabel: string;
  size?: number;
  /** 'outlined' = bordered surface (Airbnb back button), 'ghost' = no border. */
  variant?: 'outlined' | 'ghost' | 'elevated';
  color?: string;
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;
};

/** Round icon button used for back, close, theme toggle and toolbar actions. */
export default function IconCircleButton({
  icon,
  onPress,
  accessibilityLabel,
  size = 40,
  variant = 'outlined',
  color,
  style,
  disabled,
}: IconCircleButtonProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      hitSlop={4}
      style={(state) => {
        const hovered = (state as { hovered?: boolean }).hovered;
        return [
          styles.base,
          { width: size, height: size, borderRadius: size / 2 },
          variant === 'outlined' ? styles.outlined : null,
          variant === 'elevated' ? [styles.outlined, shadow(colors, 2)] : null,
          hovered || state.pressed ? styles.hovered : null,
          disabled ? styles.disabled : null,
          style,
        ];
      }}
    >
      <MaterialCommunityIcons name={icon} size={Math.round(size * 0.48)} color={color ?? colors.textPrimary} />
    </Pressable>
  );
}

const useStyles = makeStyles((c) => ({
  base: { alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' },
  outlined: { backgroundColor: c.surface, borderWidth: 1, borderColor: c.divider },
  hovered: { backgroundColor: c.surfaceAlt },
  disabled: { opacity: 0.4 },
}));
